import { useMemo } from 'react';
import { useQueries, useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  INTERVALO_DO_USO_MS,
  ROTAS,
  TETO_IDS_USO_SESSOES,
  UsoPainelSchema,
  UsoSessoesSchema,
  type UsoPainel,
  type UsoSessoes,
} from '@remoto/shared';
import { chamar } from '../api';

/**
 * As duas consultas de uso do painel (ADR-0021).
 *
 * As respostas passam pelo schema do contrato na chegada: se a API e o painel
 * divergirem, a tela mostra erro em vez de desenhar `undefined` como zero —
 * um "0%" vindo de campo renomeado seria a pior mentira possível nesta tela.
 *
 * O cache morre com a sessão de login (`estado/cache.ts`) como qualquer outra
 * consulta: nada a fazer aqui além de usar o `queryClient` padrão.
 */

/**
 * A cota muda devagar, e quem a mede é a máquina: um minuto basta. O número é
 * o do contrato porque a API guarda os grupos de uso pela MESMA validade.
 */
const INTERVALO_MS = INTERVALO_DO_USO_MS;

/** `GET /uso/painel`: a tela de Uso inteira. */
export function usarUsoDoPainel(): UseQueryResult<UsoPainel> {
  return useQuery({
    queryKey: ['uso-painel'],
    queryFn: async () => UsoPainelSchema.parse(await chamar<unknown>(ROTAS.uso.painel)),
    refetchInterval: INTERVALO_MS,
  });
}

/**
 * Ids em lotes que cabem na rota: ordenados e sem repetição.
 *
 * Ordenados para a chave da consulta não mudar quando só a ordem da lista
 * muda — reordenar a lateral não pode disparar outra requisição.
 */
export function lotesDeIds(ids: readonly string[], teto = TETO_IDS_USO_SESSOES): string[][] {
  const unicos = [...new Set(ids)].sort();
  const lotes: string[][] = [];
  for (let i = 0; i < unicos.length; i += teto) lotes.push(unicos.slice(i, i + teto));
  return lotes;
}

function juntarLotes(resultados: UseQueryResult<UsoSessoes>[]): UsoSessoes {
  return Object.assign({}, ...resultados.map((r) => r.data ?? {})) as UsoSessoes;
}

/**
 * `GET /uso/sessoes?ids=`: o % e o contexto de algumas sessões, para o
 * cabeçalho da conversa e o selo da lista.
 *
 * Devolve um mapa só, com todos os lotes juntos. Id que a API não devolve —
 * fora do escopo, inexistente, ou API que ainda não tem a rota — simplesmente
 * não está no mapa, e quem desenha não desenha nada para ele.
 */
export function usarUsoDasSessoes(ids: readonly string[]): UsoSessoes {
  const chave = [...ids].sort().join(',');
  const lotes = useMemo(() => lotesDeIds(chave ? chave.split(',') : []), [chave]);

  return useQueries({
    queries: lotes.map((lote) => ({
      queryKey: ['uso-sessoes', lote.join(',')],
      queryFn: async () =>
        UsoSessoesSchema.parse(await chamar<unknown>(`${ROTAS.uso.sessoes}?ids=${lote.join(',')}`)),
      refetchInterval: INTERVALO_MS,
      staleTime: 30_000,
    })),
    combine: juntarLotes,
  });
}
